'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useApiCustomers } from '@/components/providers/v1-cutsomer-provider';

interface CustomersTableFilters {
  page: number;
  limit: number;
  name_query: string;
  phone_query: string;
  address_query: string;
  external_id: string;
  status: 'ACTIVE' | 'INACTIVE' | null;
}

interface CustomersTableCtx {
  ids: string[];
  total: number;
  pages: number;
  filters: CustomersTableFilters;
  setFilters: (v: Partial<CustomersTableFilters>) => void;
  refetch: () => void;
  activeId: string | null;
  setActiveId: (v: string | null) => void;
  selectedIds: string[];
  setSelectedIds: (v: string[]) => void;
  isLoading: boolean;
  isFetching: boolean;
}

const CustomersTableCtx = createContext<CustomersTableCtx | null>(null);

export function useCustomersTableCtx() {
  const ctx = useContext(CustomersTableCtx);
  if (!ctx) {
    throw new Error('useCustomersTableCtx must be used within a CustomersTableProvider');
  }
  return ctx;
}

export function CustomersTableProvider({ children }: { children: React.ReactNode }) {
  const [filters, setFilters] = useState<CustomersTableFilters>({
    page: 1,
    limit: 20,
    name_query: '',
    phone_query: '',
    address_query: '',
    external_id: '',
    status: 'ACTIVE',
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useApiCustomers({
    name_query: filters.name_query,
    phone_query: filters.phone_query,
    address_query: filters.address_query,
    external_id: filters.external_id,
    status: filters.status,
    limit: filters.limit,
    page: filters.page,
  });
  const ids = useMemo(() => data?.items.map((customer) => customer.id) || [], [data]);
  const total = data?.total || 0;
  const pages = Math.ceil(total / filters.limit);

  const onSetFilters = useCallback((v: Partial<CustomersTableFilters>) => {
    setFilters((prev) => ({ ...prev, ...v }));
  }, []);

  return (
    <CustomersTableCtx.Provider
      value={{
        ids,
        pages,
        total,
        filters,
        setFilters: onSetFilters,
        refetch,
        activeId,
        setActiveId,
        selectedIds,
        setSelectedIds,
        isLoading,
        isFetching,
      }}
    >
      {children}
    </CustomersTableCtx.Provider>
  );
}
